function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// ----------------------------------------------------------------------------
// Debug Utility
// ----------------------------------------------------------------------------
//
// Shared logging utility for application code.
//
// Use this instead of adding new raw `console.*` calls in normal application
// code. Debug output can remain in production code because it is gated by
// environment-aware verbosity checks.
//
// Use Debug for:
// - initialization milestones
// - state transitions
// - event payloads at major lifecycle boundaries
// - filtered or transformed values
// - unusual but recoverable states
//
// Avoid:
// - logging on every line unless using `Debug.extreme()` for an intentional
//   short-term deep trace
// - logging secrets, tokens, or sensitive customer data
// - mixing ad hoc `console.log` with Debug in the same flow
// - using logs instead of real error handling
//
// Verbosity levels:
// 0 - Off
// 1 - Important milestones / unexpected states
// 2 - Meaningful data snapshots / mutation details
// 3 - Step-by-step flow tracing
// 4 - Extremely noisy tracing
//
// Helpers:
// Debug.message('Cart updated', { cart });            // Level 1
// Debug.details('Product Form Data ↓', data);         // Level 2
// Debug.verbose('Evaluating availability', payload);  // Level 3
// Debug.extreme('Observer mutation', payload);        // Level 4
//
// Use `Debug.warn()` and `Debug.error()` for noteworthy issues that should
// remain visible when debugging is enabled.
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import colors from 'scripts/libs/colors';

// -------------------------------------
// Functionality
// -------------------------------------

export default class Debug {
  static initialize() {
    var debugSetting = localStorage.getItem('iamotaDebug');

    // Check if iamotaDebug is already set
    if (debugSetting !== null) {
      this.setVerbosity(parseInt(debugSetting, 10));
    } else {
      var _window$Shopify;
      // Determine if debug should be enabled by default
      // i.e. if on a Shopify domain or in an unpublished theme
      if (window.location.hostname.includes('myshopify.com') || ((_window$Shopify = window.Shopify) === null || _window$Shopify === void 0 || (_window$Shopify = _window$Shopify.theme) === null || _window$Shopify === void 0 ? void 0 : _window$Shopify.role) === 'unpublished') {
        var _window$Shopify2;
        // Enable level 1 if the theme name includes (Dev), otherwise level 3
        if ((_window$Shopify2 = window.Shopify) !== null && _window$Shopify2 !== void 0 && (_window$Shopify2 = _window$Shopify2.theme) !== null && _window$Shopify2 !== void 0 && _window$Shopify2.name && window.Shopify.theme.name.includes('(Dev)')) {
          this.enable(1);
        } else {
          this.enable(3);
        }
      }
    }

    // Expose Debug class to the window object
    window.Debug = this;
  }
  static _output(message) {
    var target = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : console.log;
    var verbosity = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;
    if (verbosity <= this.verbosity) {
      for (var _len = arguments.length, additionalData = new Array(_len > 3 ? _len - 3 : 0), _key = 3; _key < _len; _key++) {
        additionalData[_key - 3] = arguments[_key];
      }
      if (target === console.log) {
        if (typeof message === 'string') {
          console.groupCollapsed("".concat(this._getPrefix(verbosity), " ").concat(message));
        } else {
          console.groupCollapsed("".concat(this._getPrefix(verbosity), " ").concat(JSON.stringify(message)));
          console.log(message);
        }
        console.log(...additionalData);
        console.trace();
        console.groupEnd();
      } else {
        if (typeof message === 'string') {
          target("".concat(this._getPrefix(verbosity), " ").concat(message), ...additionalData);
        } else {
          target(this._getPrefix(verbosity), message, ...additionalData);
        }
      }
    }
  }
  static log(message) {
    var verbosity = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
    for (var _len2 = arguments.length, additionalData = new Array(_len2 > 2 ? _len2 - 2 : 0), _key2 = 2; _key2 < _len2; _key2++) {
      additionalData[_key2 - 2] = arguments[_key2];
    }
    this._output(message, console.log, verbosity, ...additionalData);
  }
  static warn(message) {
    for (var _len3 = arguments.length, additionalData = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
      additionalData[_key3 - 1] = arguments[_key3];
    }
    this._output(message, console.warn, 1, ...additionalData);
  }
  static error(message) {
    for (var _len4 = arguments.length, additionalData = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
      additionalData[_key4 - 1] = arguments[_key4];
    }
    this._output(message, console.error, 1, ...additionalData);
  }
  static message(message) {
    for (var _len5 = arguments.length, additionalData = new Array(_len5 > 1 ? _len5 - 1 : 0), _key5 = 1; _key5 < _len5; _key5++) {
      additionalData[_key5 - 1] = arguments[_key5];
    }
    this.log(message, 1, ...additionalData);
  }
  static details(message) {
    for (var _len6 = arguments.length, additionalData = new Array(_len6 > 1 ? _len6 - 1 : 0), _key6 = 1; _key6 < _len6; _key6++) {
      additionalData[_key6 - 1] = arguments[_key6];
    }
    this.log(message, 2, ...additionalData);
  }
  static verbose(message) {
    for (var _len7 = arguments.length, additionalData = new Array(_len7 > 1 ? _len7 - 1 : 0), _key7 = 1; _key7 < _len7; _key7++) {
      additionalData[_key7 - 1] = arguments[_key7];
    }
    this.log(message, 3, ...additionalData);
  }
  static extreme(message) {
    for (var _len8 = arguments.length, additionalData = new Array(_len8 > 1 ? _len8 - 1 : 0), _key8 = 1; _key8 < _len8; _key8++) {
      additionalData[_key8 - 1] = arguments[_key8];
    }
    this.log(message, 4, ...additionalData);
  }
  static enable() {
    var level = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1;
    this.setVerbosity(level);
  }
  static disable() {
    this.setVerbosity(0);
  }
  static setVerbosity(level) {
    this.verbosity = level;
    localStorage.setItem('iamotaDebug', String(level));
  }
  static _getPrefix(verbosity) {
    switch (verbosity) {
      case 1:
        return colors.blue("[Debug:".concat(verbosity, "]"));
      case 2:
        return colors.blueBright("[Debug:".concat(verbosity, "]"));
      case 3:
        return colors.cyan("[Debug:".concat(verbosity, "]"));
      case 4:
        return colors.gray("[Debug:".concat(verbosity, "]"));
      case 5:
        return colors.yellow("[Debug:".concat(verbosity, "]"));
      default:
        return colors.red("[Debug:".concat(verbosity, "]"));
    }
  }
}

// Initialize debug mode based on conditions
_defineProperty(Debug, "verbosity", 0);
_defineProperty(Debug, "colors", colors);
Debug.initialize();